import type { AfgorelseType } from "@/interfaces/afgorelse";
import type { PeriodeLektioner } from "@/interfaces/efteruddannelse";
import type { PreparePaabudIndberetningRequest } from "@/interfaces/paabudIndberetning";
import type { PreparePaalaegIndberetningRequest } from "@/interfaces/paalaegIndberetning";
import { useDownloadPaabudDocument } from "@/hooks/usePaabud";
import { usePreparePaabudIndberetningForSigning } from "@/hooks/usePaabudIndberetning";
import {
  usePendingPaabudIndberetningEvidence,
  useReportEvidenceByPaabud,
} from "@/hooks/usePaabudIndberetning";
import { useDownloadPaalaegDocument } from "@/hooks/usePaalaeg";
import { usePreparePaalaegIndberetningForSigning } from "@/hooks/usePaalaegIndberetning";
import {
  usePendingPaalaegIndberetningEvidence,
  useReportEvidenceByPaalaeg,
} from "@/hooks/usePaalaegIndberetning";

export type PrepareAfgorelseIndberetningInput = {
  id: string;
  afgorelseId: string;
  lektionerIndberettet: PeriodeLektioner;
  lektionerOverfoeresTilNaeste: PeriodeLektioner;
  kvitteringEmails: string[];
  evidenceItemIds: string[];
};

export const usePrepareAfgorelseIndberetningForSigning = (
  afgorelseType: AfgorelseType,
) => {
  const paabudMutation = usePreparePaabudIndberetningForSigning();
  const paalaegMutation = usePreparePaalaegIndberetningForSigning();

  const mutation =
    afgorelseType === "paabud" ? paabudMutation : paalaegMutation;

  return {
    ...mutation,
    mutateAsync: async (input: PrepareAfgorelseIndberetningInput) => {
      if (afgorelseType === "paabud") {
        const request: PreparePaabudIndberetningRequest = {
          id: input.id,
          paabudId: input.afgorelseId,
          lektionerIndberettet: input.lektionerIndberettet,
          lektionerOverfoeresTilNaeste: input.lektionerOverfoeresTilNaeste,
          kvitteringEmails: input.kvitteringEmails,
          evidenceItemIds: input.evidenceItemIds,
        };

        return paabudMutation.mutateAsync(request);
      }

      const request: PreparePaalaegIndberetningRequest = {
        id: input.id,
        paalaegId: input.afgorelseId,
        lektionerIndberettet: input.lektionerIndberettet,
        lektionerOverfoeresTilNaeste: input.lektionerOverfoeresTilNaeste,
        kvitteringEmails: input.kvitteringEmails,
        evidenceItemIds: input.evidenceItemIds,
      };

      return paalaegMutation.mutateAsync(request);
    },
  };
};

export const useReportEvidenceByAfgorelse = (
  afgorelseType: AfgorelseType,
  afgorelseId?: string | null,
) => {
  const paabudEvidence = useReportEvidenceByPaabud(
    afgorelseType === "paabud" ? afgorelseId : null,
  );
  const paalaegEvidence = useReportEvidenceByPaalaeg(
    afgorelseType === "paalaeg" ? afgorelseId : null,
  );

  return afgorelseType === "paabud" ? paabudEvidence : paalaegEvidence;
};

/**
 * Kladdens valgte dokumentation. Følger samme mønster som useReportEvidenceByAfgorelse:
 * begge hooks kaldes ubetinget og den inaktive slås fra med null, så hook-rækkefølgen
 * er stabil på tværs af renders.
 */
export const usePendingAfgorelseEvidence = (
  afgorelseType: AfgorelseType,
  indberetningId?: string | null,
) => {
  const paabudPending = usePendingPaabudIndberetningEvidence(
    afgorelseType === "paabud" ? indberetningId : null,
  );
  const paalaegPending = usePendingPaalaegIndberetningEvidence(
    afgorelseType === "paalaeg" ? indberetningId : null,
  );

  return afgorelseType === "paabud" ? paabudPending : paalaegPending;
};

export const useDownloadAfgorelseDocument = (afgorelseType: AfgorelseType) => {
  const paabudDownload = useDownloadPaabudDocument();
  const paalaegDownload = useDownloadPaalaegDocument();

  return afgorelseType === "paabud" ? paabudDownload : paalaegDownload;
};
